/**
 * OrbitPrint — app entry (third product, own auth realm).
 *
 * Mounted two ways by App.js:
 *   orbitprint.agri-books.com  →  <OrbitApp base="" />        (owns the whole host)
 *   anywhere else         →  <Route path="/orbit/*" element={<OrbitApp base="/orbit" />} />
 * Routes below are relative; pages build absolute links through useOrbitPath().
 */
import { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '../components/ui/sonner';
import { OrbitAuthProvider, useOrbitAuth, useOrbitPath } from './OrbitAuthContext';
import { OrbitLiveProvider } from './live';
import { PlanProvider } from './plan';
import { OrbitLoginPage, OrbitRegisterPage, OrbitForgotPasswordPage, OrbitResetPasswordPage } from './pages/OrbitAuthPages';
import OrbitDashboardPage from './pages/OrbitDashboardPage';
import OrbitMembersPage from './pages/OrbitMembersPage';
import OrbitAcceptInvitePage from './pages/OrbitAcceptInvitePage';
import OrbitPlansPage from './pages/OrbitPlansPage';
import './orbit.css';

/** The amber ribbon on every page while a super-admin support session is open in this tab. */
function SupportRibbon() {
  const { supportSession, org, endSupportSession } = useOrbitAuth();
  if (!supportSession) return null;
  const ends = supportSession.expiresAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  return (
    <div data-testid="orbit-support-ribbon" role="status"
      style={{ background: 'var(--ob-amber)', color: 'var(--ob-amber-ink)', fontSize: 13, fontWeight: 700, padding: '8px 16px',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, flexWrap: 'wrap' }}>
      <span>Support session · you are viewing <strong>{org?.name || 'this organization'}</strong> as its owner
        {supportSession.by ? ` · opened by ${supportSession.by}` : ''} · ends {ends}</span>
      <button onClick={endSupportSession} data-testid="orbit-support-end"
        style={{ background: 'var(--ob-amber-ink)', color: 'var(--ob-amber)', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 800, cursor: 'pointer' }}>
        End session
      </button>
    </div>
  );
}

function Protected({ children }) {
  const { user, loading } = useOrbitAuth();
  const p = useOrbitPath();
  if (loading) {
    return (
      <div className="orbit-root min-h-screen grid place-items-center">
        <span className="orbit-mono text-sm" style={{ color: 'var(--ob-ink-faint)' }}>Loading…</span>
      </div>
    );
  }
  if (!user) return <Navigate to={p('/login')} replace />;
  return <><SupportRibbon />{children}</>;
}

function OrbitRoutes() {
  return (
    <Routes>
      <Route path="login" element={<OrbitLoginPage />} />
      <Route path="register" element={<OrbitRegisterPage />} />
      <Route path="forgot-password" element={<OrbitForgotPasswordPage />} />
      <Route path="reset-password" element={<OrbitResetPasswordPage />} />
      <Route path="accept-invite" element={<OrbitAcceptInvitePage />} />
      <Route path="plans" element={<OrbitPlansPage />} />
      <Route path="members" element={<Protected><OrbitMembersPage /></Protected>} />
      <Route path="" element={<Protected><OrbitDashboardPage /></Protected>} />
      <Route path="*" element={<Protected><OrbitDashboardPage /></Protected>} />
    </Routes>
  );
}

export default function OrbitApp({ base = '/orbit' }) {
  // The shared index.html carries the AgriBooks title; this product has its own.
  useEffect(() => {
    const prev = document.title;
    document.title = 'OrbitPrint';
    return () => { document.title = prev; };
  }, []);
  return (
    <OrbitAuthProvider base={base}>
      <OrbitLiveProvider>
        <PlanProvider>
          <OrbitRoutes />
          <Toaster position="bottom-right" richColors />
        </PlanProvider>
      </OrbitLiveProvider>
    </OrbitAuthProvider>
  );
}
